import { useState } from "react";
import { Icon } from "../common/ui";
import Lightbox from "../common/Lightbox";

/**
 * Structured lesson content renderer. `content` is a free-form JSONB blob
 * (see migration 006) shaped as { blocks: Block[] }. Each block is bilingual.
 */
type Bi = { ru: string; en: string };
type BiList = { ru: string[]; en: string[] };

type Block =
  | ({ type: "paragraph" } & Bi)
  | ({ type: "list" } & BiList)
  | { type: "steps"; items: ({ n: number } & Bi)[] }
  | { type: "pairs"; title_ru?: string; title_en?: string; items: { k_ru: string; k_en: string; v_ru: string; v_en: string }[] }
  | ({ type: "callout"; tone?: "tip" | "warn" } & Bi)
  // `src_en` is for artwork whose TEXT is baked in (checklists, posters):
  // those cannot be translated by swapping a caption, only by supplying a
  // separately drawn file. Falls back to `src` when absent.
  | { type: "image"; src: string; src_en?: string; caption_ru?: string; caption_en?: string }
  | { type: "ref"; ru: string; en?: string }
  // Marker, not content: says WHERE the lesson video sits in the flow.
  // The expert's order is description -> video -> the numbered steps, and
  // that differs per lesson, so it lives in the data rather than in layout.
  | { type: "video" };

interface ContentShape {
  blocks?: Block[];
}

/** True when the lesson positions its own video via a `video` block. */
export function hasVideoBlock(content: Record<string, unknown> | null | undefined): boolean {
  const blocks = (content as ContentShape | undefined)?.blocks;
  return !!blocks?.some((b) => b.type === "video");
}

export default function LessonContent({
  content, isRu, videoSlot,
}: {
  content: Record<string, unknown> | null | undefined;
  isRu: boolean;
  /** Rendered in place of the `video` marker block. */
  videoSlot?: React.ReactNode;
}) {
  const blocks = (content as ContentShape | undefined)?.blocks;
  const [lightbox, setLightbox] = useState<string | null>(null);
  if (!blocks || blocks.length === 0) return null;
  const pick = (ru: string, en: string) => (isRu ? ru : en || ru);

  return (
    // NB: `.stack > * + *` resolves --gap on the CHILD, so blocks must not set
    // their own --gap (it shrinks the space above them). Inner lists use flex
    // gap instead of the .stack class for that reason.
    <div className="stack" style={{ "--gap": "18px", marginBottom: 18 } as React.CSSProperties}>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {blocks.map((b, i) => {
        switch (b.type) {
          case "paragraph":
            return (
              <p key={i} className="t-sm" style={{ fontWeight: 600, lineHeight: 1.55, marginBottom: 0 }}>
                {pick(b.ru, b.en)}
              </p>
            );

          case "list": {
            const items = isRu ? b.ru : (b.en?.length ? b.en : b.ru);
            return (
              <ul key={i} style={{ display: "flex", flexDirection: "column", gap: 8, listStyle: "none", marginInline: 0, marginBottom: 0, padding: 0 }}>
                {items.map((it, j) => (
                  <li key={j} className="row" style={{ alignItems: "flex-start", gap: 10 }}>
                    <span style={{ color: "var(--accent-text)", marginTop: 2, flex: "none" }}>
                      <Icon name="check" size={15} stroke={2.6} />
                    </span>
                    <span className="t-sm" style={{ fontWeight: 600, lineHeight: 1.45 }}>{it}</span>
                  </li>
                ))}
              </ul>
            );
          }

          case "steps":
            return (
              <ol key={i} style={{ display: "flex", flexDirection: "column", gap: 8, listStyle: "none", marginInline: 0, marginBottom: 0, padding: 0 }}>
                {b.items.map((s) => (
                  <li key={s.n} className="glass-1 glass" style={{ padding: 12, borderRadius: 13, display: "flex", gap: 12 }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: 8, flex: "none", display: "grid", placeItems: "center",
                      background: "var(--violet-tint)", color: "var(--violet-text)", fontWeight: 800, fontSize: "0.82rem",
                    }}>{s.n}</span>
                    <span className="t-sm" style={{ fontWeight: 600, lineHeight: 1.45 }}>{pick(s.ru, s.en)}</span>
                  </li>
                ))}
              </ol>
            );

          case "pairs": {
            const title = pick(b.title_ru || "", b.title_en || "");
            return (
              <div key={i} className="glass-1 glass" style={{ padding: 14, borderRadius: 14 }}>
                {title && <div className="t-xs" style={{ color: "var(--accent-text)", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 8 }}>{title}</div>}
                <div className="stack" style={{ "--gap": "0px" } as React.CSSProperties}>
                  {b.items.map((p, j) => (
                    <div key={j} className="between" style={{ gap: 12, padding: "8px 0", borderTop: j ? "1px solid var(--border)" : "none", alignItems: "flex-start" }}>
                      <span className="t-sm muted" style={{ fontWeight: 700, flex: "0 0 42%" }}>{pick(p.k_ru, p.k_en)}</span>
                      <span className="t-sm t-mono" style={{ fontWeight: 700, textAlign: "right", flex: 1 }}>{pick(p.v_ru, p.v_en)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          case "callout": {
            const warn = b.tone === "warn";
            return (
              <div key={i} className="row" style={{
                gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 13,
                background: warn ? "var(--accent-tint)" : "var(--violet-tint)",
                color: warn ? "var(--accent-text)" : "var(--violet-text)",
              }}>
                <span style={{ flex: "none", marginTop: 1 }}>
                  <Icon name={warn ? "alert" : "sparkles"} size={16} stroke={2.2} />
                </span>
                <span className="t-sm" style={{ fontWeight: 700, lineHeight: 1.45 }}>{pick(b.ru, b.en)}</span>
              </div>
            );
          }

          case "image": {
            const caption = pick(b.caption_ru || "", b.caption_en || "");
            const src = isRu ? b.src : (b.src_en || b.src);
            return (
              <figure key={i} style={{ marginInline: 0, marginBottom: 0 }}>
                <img
                  src={src}
                  alt={caption || "lesson illustration"}
                  loading="lazy"
                  onClick={() => setLightbox(src)}
                  style={{ width: "100%", height: "auto", display: "block", borderRadius: 14, border: "1px solid var(--border)", background: "var(--glass-1)", cursor: "zoom-in" }}
                />
                {caption && (
                  <figcaption className="t-xs muted-2" style={{ marginTop: 6, textAlign: "center" }}>{caption}</figcaption>
                )}
              </figure>
            );
          }

          case "video":
            return videoSlot ? <div key={i}>{videoSlot}</div> : null;

          case "ref":
            return (
              <p key={i} className="t-xs muted-2" style={{ marginInline: 0, marginBottom: 0, fontStyle: "italic", opacity: 0.8 }}>
                {pick(b.ru, b.en || b.ru)}
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
